import { AircraftModelsService } from './aircraft-models.service'
import { AircraftMakesService } from './aircraft-makes.service'
import { AircraftModel } from '../../types/intellijetnet'

interface ModelInfo {
  model_id: number
  make_name: string
  model_name: string
  display_name: string
}

export class ModelInfoCacheService {
  private static cache: Map<number, ModelInfo> = new Map()
  private static makesCache: Map<number, string> = new Map()
  private static isInitialized = false

  /**
   * Initialize the cache by loading all makes
   */
  private static async initializeMakes(): Promise<void> {
    if (this.isInitialized) return

    try {
      const makes = await AircraftMakesService.getAll()
      makes.forEach(make => {
        this.makesCache.set(make.make_id, make.make_name)
      })
      this.isInitialized = true
    } catch (error) {
      // Failed to initialize makes cache - silently ignore
    }
  }

  /**
   * Get model info for a single model ID
   */
  static async getModelInfo(modelId: number): Promise<ModelInfo | null> {
    // Check cache first
    if (this.cache.has(modelId)) {
      return this.cache.get(modelId)!
    }

    try {
      await this.initializeMakes()
      
      const model = await AircraftModelsService.getById(modelId)
      if (!model) return null

      const makeName = model.make_id ? this.makesCache.get(model.make_id) || 'Unknown' : 'Unknown'
      
      const modelInfo: ModelInfo = {
        model_id: modelId,
        make_name: makeName,
        model_name: model.model,
        display_name: `${makeName} ${model.model}`
      }

      this.cache.set(modelId, modelInfo)
      return modelInfo
    } catch (error) {
      return null
    }
  }

  /**
   * Get model info for multiple model IDs
   */
  static async getModelsInfo(modelIds: number[]): Promise<ModelInfo[]> {
    if (modelIds.length === 0) return []

    await this.initializeMakes()

    // Separate cached and uncached IDs
    const result: ModelInfo[] = []
    const uncachedIds: number[] = []

    for (const id of modelIds) {
      if (this.cache.has(id)) {
        result.push(this.cache.get(id)!)
      } else {
        uncachedIds.push(id)
      }
    }

    // Fetch uncached models
    if (uncachedIds.length > 0) {
      try {
        const models = await AircraftModelsService.query()
          .in('model_id', uncachedIds)
          .execute<AircraftModel>()

        for (const model of models.data) {
          const makeName = model.make_id ? this.makesCache.get(model.make_id) || 'Unknown' : 'Unknown'
          
          const modelInfo: ModelInfo = {
            model_id: model.model_id,
            make_name: makeName,
            model_name: model.model,
            display_name: `${makeName} ${model.model}`
          }

          this.cache.set(model.model_id, modelInfo)
          result.push(modelInfo)
        }
      } catch (error) {
        // Failed to fetch models info - silently ignore
      }
    }

    return result
  }

  /**
   * Get display names for model IDs
   */
  static async getDisplayNames(modelIds: number[]): Promise<string[]> {
    const modelsInfo = await this.getModelsInfo(modelIds)
    return modelsInfo.map(info => info.display_name).sort()
  }

  /**
   * Clear the cache
   */
  static clearCache(): void {
    this.cache.clear()
    this.makesCache.clear()
    this.isInitialized = false
  }
}